<template>
  <div class="rounded-md bg-white p-4">
    <div class="mb-4 space-y-4">
      <div class="grid grid-cols-2 gap-4">
        <SelectField class="w-full" name="type" :label="t('invoiceType')" :options="typeOptions" />
        <SelectField
          v-if="counterpartyTypeOptions.length > 1"
          class="w-full"
          name="counterpartyType"
          :label="t('counterpartyType')"
          :options="counterpartyTypeOptions"
          keep-value-on-unmount
        />
        <SelectField
          v-if="formValues.type === InvoiceType.REVERSAL"
          class="w-full"
          name="originalInvoiceId"
          :label="t('originalInvoice')"
          :options="originalInvoicesOptions"
        />
        <div v-if="formValues.type === InvoiceType.ADMINISTRATIVE_OFFENCE" class="mt-[2.1rem]">
          <Checkbox v-model="isInternationalAdministrativeOffence">
            {{ t('international') }}
          </Checkbox>
        </div>
        <InputField name="title" :label="t('invoiceTitle')" class="col-start-1" />
        <TaxProcedureSelect
          class="col-start-1"
          :model-value="formValues.taxProcedure"
          :vat-id="counterpartyVatId"
          :require-vat-id-for-reverse-charge="formValues.type !== InvoiceType.REVERSAL"
          @update:model-value="(value) => setFieldValue('taxProcedure', value, false)"
        />
      </div>
      <div class="mt-4 text-base font-medium lg:text-lg">
        {{ t('positions') }}
      </div>
      <div>
        <div v-for="(position, i) in positionFields" :key="i" class="relative">
          <div
            class="mb-4 grid grid-cols-1 gap-4 border-b border-obsidian-300 pb-4 sm:grid-cols-2"
            :class="formValues.positions[i]?.lane ? 'xl:grid-cols-4' : 'xl:grid-cols-3'"
          >
            <TextareaField
              :name="`positions[${i}].title`"
              :label="t('title')"
              class="sm:col-span-2"
              :class="formValues.positions[i]?.lane ? 'xl:col-span-4' : 'xl:col-span-3'"
            />
            <InputField
              :name="`positions[${i}].amount`"
              :label="t('amount')"
              type="number"
              :max-fraction-digits="0"
            />
            <InputField
              :name="`positions[${i}].unitPrice`"
              :label="t('unitPrice')"
              type="number"
              :max-fraction-digits="2"
              adornment="€"
            />
            <InputField
              :disabled="isPositionTaxDisabled"
              :name="`positions[${i}].tax`"
              :label="t('tax')"
              type="number"
              :max-fraction-digits="0"
              adornment="%"
            />
            <InputField
              v-if="formValues.positions[i]?.lane"
              :name="`positions[${i}].lane`"
              :label="t('lanePreset')"
              readonly
            />
          </div>
          <div v-if="i !== 0" class="absolute right-0 top-0 flex items-center">
            <button
              class="cursor-pointer p-2 text-lg leading-3 text-red-600"
              @click="removePosition(i)"
            >
              ×
            </button>
          </div>
        </div>
        <div class="mt-4 flex justify-end">
          <CVButton @click.prevent="pushPosition(getEmptyPosition())">{{
            t('addPosition')
          }}</CVButton>
        </div>
      </div>
      <div class="border-b border-obsidian-300 pb-4">
        <div class="flex items-center gap-4">
          <Select
            v-model="selectedPaymentId"
            class="w-full"
            :label="t('paymentsMade')"
            :options="paymentsOptions"
            empty-value-to="null"
            @change="addPaymentId"
          />
        </div>
        <div v-if="selectedPayments.length > 0" class="mt-2 flex justify-start gap-3">
          <div
            v-for="(payment, i) in selectedPayments"
            :key="i"
            class="rounded-full bg-primary px-3 py-0.5 text-white"
          >
            {{ paymentToHuman(payment) }}
            <button
              class="cursor-pointer px-0.5 py-2 text-lg leading-3"
              @click.prevent="removePaymentId(i)"
            >
              ×
            </button>
          </div>
        </div>
      </div>
      <Checkbox v-model="sendInvoiceOnSave" :label="t('sendInvoiceOnSave')" />
    </div>
    <div class="flex justify-end gap-2">
      <CVButton variant="default" @click.prevent="handleSave">
        {{ t('saveInvoice') }}
      </CVButton>
      <CVButton
        v-if="formValues.status === InvoiceStatus.DRAFT"
        @click.prevent="handleSaveFinalize"
      >
        {{ t('saveAndFinalizeInvoice') }}
      </CVButton>
      <CVButton v-if="!props.hideCancelEditButton" @click.prevent="$emit('onCancel')">
        {{ t('cancel') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { InvoiceStatus } from '@/entities/invoices/invoice-status.enum';
import type { CreateInvoiceDto, Invoice } from '@/entities/invoices/invoice.entity';
import type { Payment } from '@/entities/payments/payment.entity';
import { formatDate } from '@/hooks/use-formatted-date';
import { useI18n } from 'vue-i18n';
import { formatCurrency } from '@/utils/format-numbers';
import { createInvoiceCreateSchema } from '@/validation/invoice.schema';
import { useFieldArray, useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/yup';
import { computed, ref, shallowRef, watch, watchEffect } from 'vue';
import { CounterpartyType } from '@/entities/ledger/ledger-entry.entity';
import InputField from '../InputField.vue';
import Select from '../Select.vue';
import Checkbox from '../Checkbox.vue';
import { InvoiceType } from '@/entities/invoices/invoice-type.enum';
import SelectField from '../SelectField.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import { Local } from '@/entities/auth/local.enum';
import { useDateFormat } from '@/hooks/use-date-format';
import TextareaField from '@/components/TextareaField.vue';
import CVButton from '../buttons/CVButton.vue';
import TaxProcedureSelect from '@/components/TaxProcedureSelect.vue';
import { TaxProcedure } from '@carvia/ros-client-types';
import { isNonStandardTaxProcedure } from '@/utils/tax-procedure';
import { resolveInvoiceCounterpartyType } from '@/utils/resolve-invoice-counterparty-type';
import { useCounterpartyTypeOptions } from '@/hooks/use-counterparty-type-options';

interface InvoiceFormValues {
  title: string;
  bookingId: string;
  customerId?: string;
  counterpartyType: CounterpartyType;
  taxProcedure: TaxProcedure;
  positions: PositionFormValues[];
  paymentIds: string[];
  type: InvoiceType;
  originalInvoiceId?: string;
  status: InvoiceStatus;
}

interface PositionFormValues {
  title: string;
  amount: number | undefined;
  unitPrice: number | undefined;
  tax: number | undefined;
  lane?: string;
  counterpartyType?: CounterpartyType;
}

const props = defineProps<{
  booking: Booking;
  invoices: Invoice[];
  payments: Payment[];
  invoice?: Invoice;
  hideCancelEditButton?: boolean;
}>();

const emit = defineEmits<{
  (e: 'onSubmit', newInvoice: CreateInvoiceDto, sendInvoiceOnSave: boolean): void;
  (e: 'onCancel'): void;
}>();

const { t } = useI18n();

const dateFormat = useDateFormat();
const selectedPaymentId = ref('');
const sendInvoiceOnSave = ref(true);
const isInternationalAdministrativeOffence = ref(false);

const validationSchema = shallowRef(toTypedSchema(createInvoiceCreateSchema()));

const {
  handleSubmit,
  values: formValues,
  setFieldValue,
  setValues,
} = useForm<InvoiceFormValues>({
  initialValues: getInitialFormValues(props.invoice),
  validationSchema,
});

const counterpartyVatId = computed(() => {
  if (formValues.counterpartyType === CounterpartyType.AGENCY) {
    return props.booking.agency?.vatId;
  }
  if (
    formValues.counterpartyType === CounterpartyType.CUSTOMER ||
    formValues.counterpartyType == null
  ) {
    return props.booking.customerData?.vatId;
  }
  return undefined;
});

watchEffect(() => {
  validationSchema.value = toTypedSchema(
    createInvoiceCreateSchema({
      counterpartyVatId,
    }),
  );
});

const isPositionTaxDisabled = computed(
  () =>
    formValues.type === InvoiceType.DAMAGE ||
    formValues.type === InvoiceType.CANCELLATION_FEE ||
    isNonStandardTaxProcedure(formValues.taxProcedure),
);

const {
  push: pushPosition,
  remove: removePosition,
  replace: replacePositions,
  fields: positionFields,
} = useFieldArray<PositionFormValues>('positions');

const {
  push: pushPaymentId,
  remove: removePaymentId,
  fields: paymentFields,
} = useFieldArray<string>('paymentIds');

const selectablePayments = computed(() =>
  props.payments.filter(
    (payment) =>
      !payment.invoice && !paymentFields.value.some((field) => field.value === payment.id),
  ),
);

const selectedPayments = computed(() =>
  paymentFields.value.map(
    (field) => props.payments.find((payment) => payment.id === field.value) as Payment,
  ),
);

const typeOptions = computed(() =>
  Object.values([
    InvoiceType.DAMAGE,
    InvoiceType.CANCELLATION_FEE,
    InvoiceType.ADMINISTRATIVE_OFFENCE,
    InvoiceType.REVERSAL,
    InvoiceType.OTHER,
  ]).map((type) => ({
    label: t(`invoiceTypes.${type}`),
    value: type,
  })),
);

const counterpartyTypeOptions = useCounterpartyTypeOptions(
  computed(() => {
    const types = [CounterpartyType.CUSTOMER];
    if (props.booking.agency) {
      types.push(CounterpartyType.AGENCY);
    }
    // Insurance-case bookings can bill the insurer as OTHER; also keep OTHER if already stored.
    if (props.booking.insuranceCase || formValues.counterpartyType === CounterpartyType.OTHER) {
      types.push(CounterpartyType.OTHER);
    }
    return types;
  }),
);

const originalInvoicesOptions = computed(() => [
  { label: t('selectOriginalInvoice'), value: null },
  ...props.invoices
    .filter((invoice) => invoice.type !== InvoiceType.REVERSAL)
    .map((invoice) => ({
      label: invoice.completeInvoiceNumber + (invoice.title ? ' - ' + invoice.title : ''),
      value: invoice.id,
    })),
]);

watch(
  [
    () => formValues.counterpartyType,
    () => props.booking.agency,
    () => props.booking.customer,
    () => props.booking.insuranceCase,
  ],
  ([newCounterpartyType, agency, customer, insuranceCase]) => {
    if (newCounterpartyType === CounterpartyType.AGENCY && !agency) {
      setFieldValue('counterpartyType', CounterpartyType.CUSTOMER);
      return;
    }
    if (newCounterpartyType === CounterpartyType.OTHER) {
      if (insuranceCase?.customer?.id) {
        setFieldValue('customerId', insuranceCase.customer.id);
      }
      return;
    }
    setFieldValue(
      'customerId',
      newCounterpartyType === CounterpartyType.AGENCY ? agency?.id : customer?.id,
    );
  },
  { immediate: true },
);

const paymentsOptions = computed(() => [
  { label: t('selectPayment'), value: null },
  ...selectablePayments.value.map((payment) => ({
    label: paymentToHuman(payment),
    value: payment.id,
  })),
]);

const paymentToHuman = (payment: Payment) => {
  return `${formatDate(payment.createdDate, dateFormat.value)} - ${t(
    `paymentTypes.${payment.paymentType}`,
  )} ${payment.amount >= 0 ? t('payment') : t('repayment')}: ${formatCurrency(payment.amount)}`;
};

const addPaymentId = () => {
  pushPaymentId(selectedPaymentId.value);
  selectedPaymentId.value = '';
};

const updatePositionTaxes = (tax: number) => {
  setValues(
    {
      positions: positionFields.value.map((field) => ({ ...field.value, tax })),
    },
    false,
  );
};

watch(
  () => formValues.taxProcedure,
  (procedure, previousProcedure) => {
    if (positionFields.value.length === 0) {
      return;
    }
    if (isNonStandardTaxProcedure(procedure)) {
      updatePositionTaxes(0);
    } else if (previousProcedure && isNonStandardTaxProcedure(previousProcedure)) {
      updatePositionTaxes(
        [InvoiceType.DAMAGE, InvoiceType.CANCELLATION_FEE].includes(formValues.type) ? 0 : 19,
      );
    }
  },
);

watch(
  [() => formValues.type, () => isInternationalAdministrativeOffence.value],
  ([newType, isInternational], [oldType]) => {
    if (newType === InvoiceType.DAMAGE || newType === InvoiceType.CANCELLATION_FEE) {
      replacePositions(positionFields.value.map((field) => ({ ...field.value, tax: 0 })));
    } else if (newType === InvoiceType.ADMINISTRATIVE_OFFENCE) {
      const locale = props.booking.customerData?.preferredLocal === Local.DE_DE ? 'de' : 'en';
      replacePositions([
        {
          title: t('administrativeOffencePositionTitle', 0, { locale }),
          amount: 1,
          unitPrice: isInternational ? 45 : 30,
          tax: isNonStandardTaxProcedure(formValues.taxProcedure) ? 0 : 19,
        },
      ]);
    } else if (oldType === InvoiceType.DAMAGE || oldType === InvoiceType.CANCELLATION_FEE) {
      replacePositions(
        positionFields.value.map((field) => ({
          ...field.value,
          tax: isNonStandardTaxProcedure(formValues.taxProcedure) ? 0 : 19,
        })),
      );
    }
  },
);

const saveInvoice = async (values: InvoiceFormValues) => {
  const newInvoice: CreateInvoiceDto = {
    ...values,
    // Persist explicitly so ledger does not re-infer OTHER from an insurer recipient.
    counterpartyType: values.counterpartyType ?? CounterpartyType.CUSTOMER,
    positions: values.positions.map((position) => ({
      title: position.title,
      amount: position.amount ?? 0,
      unitPrice: position.unitPrice ?? 0,
      tax: (position.tax ?? 0) / 100,
      lane: position.lane,
      counterpartyType: position.counterpartyType,
    })),
  };
  emit('onSubmit', newInvoice, sendInvoiceOnSave.value);
};

const handleSave = handleSubmit(saveInvoice);

const handleSaveFinalize = handleSubmit(async (values: InvoiceFormValues) => {
  values.status = InvoiceStatus.OPEN;
  await saveInvoice(values);
});

function getInitialFormValues(invoice?: Invoice): InvoiceFormValues {
  return {
    title: invoice?.title ?? '',
    bookingId: invoice?.booking?.id ?? props.booking.id,
    // New manuals default to the booking customer. The insurer recipient is applied
    // only when Gegenpartei OTHER is selected (see watcher below).
    customerId: invoice?.customer?.id ?? props.booking.customer?.id,
    // New manual invoices default to CUSTOMER; OTHER is only kept when already stored.
    counterpartyType: invoice
      ? (resolveInvoiceCounterpartyType({
          counterpartyType: invoice.counterpartyType,
          customer: invoice.customer ?? null,
          booking: invoice.booking ?? props.booking,
        }) ?? CounterpartyType.CUSTOMER)
      : CounterpartyType.CUSTOMER,
    positions: invoice?.positions.map((p) => ({
      ...p,
      tax: p.tax * 100,
    })) ?? [getEmptyPosition(InvoiceType.DAMAGE)],
    paymentIds: invoice?.payments?.map((payment) => payment.id) ?? [],
    type: invoice?.type ?? InvoiceType.DAMAGE,
    originalInvoiceId: undefined,
    status: invoice?.status ?? InvoiceStatus.OPEN,
    taxProcedure: invoice?.taxProcedure ?? props.booking.taxProcedure ?? TaxProcedure.REGULAR,
  };
}

function getEmptyPosition(type?: InvoiceType): PositionFormValues {
  const invoiceType = type ?? formValues?.type;
  return {
    title: '',
    amount: 1,
    unitPrice: undefined,
    tax:
      [InvoiceType.DAMAGE, InvoiceType.CANCELLATION_FEE].includes(invoiceType) ||
      isNonStandardTaxProcedure(formValues.taxProcedure)
        ? 0
        : 19,
    lane: undefined,
    counterpartyType: undefined,
  };
}
</script>

<i18n lang="json">
{
  "en": {
    "invoiceType": "Invoice Type",
    "counterpartyType": "Invoice recipient (counterparty)",
    "originalInvoice": "Original Invoice",
    "selectOriginalInvoice": "- Select Original Invoice -",
    "title": "Title",
    "amount": "Amount",
    "unitPrice": "Unit Price (Gross)",
    "tax": "Tax",
    "lanePreset": "Ledger lane",
    "counterpartyPreset": "Counterparty",
    "addPosition": "+ Add Position",
    "saveInvoice": "Save Invoice",
    "selectPayment": "- Select Payment -",
    "payment": "Payment",
    "repayment": "Repayment",
    "addPayment": "+ Add Payment",
    "sendInvoiceOnSave": "Automatically send Invoice to Customer after Saving",
    "compensationInvoice": "This is a damage compensation Invoice",
    "positions": "Positions",
    "invoiceTitle": "Invoice Title",
    "paymentsMade": "Payments made",
    "administrativeOffencePositionTitle": "Processing fee administrative offence booking file number ",
    "saveAndFinalizeInvoice": "Save and finalize invoice",
    "international": "International"
  },
  "de": {
    "invoiceType": "Rechnungstyp",
    "counterpartyType": "Rechnungsempfänger (Gegenpartei)",
    "originalInvoice": "Originalrechnung",
    "selectOriginalInvoice": "- Originalrechnung auswählen -",
    "title": "Titel",
    "amount": "Anzahl",
    "unitPrice": "Preis (Brutto)",
    "tax": "Steuer",
    "lanePreset": "Ledger-Lane",
    "addPosition": "+ Position hinzufügen",
    "saveInvoice": "Rechnung speichern",
    "selectPayment": "- Zahlung auswählen -",
    "payment": "Zahlung",
    "repayment": "Rückzahlung",
    "addPayment": "+ Zahlung hinzufügen",
    "sendInvoiceOnSave": "Rechnung beim Speichern automatisch an Kunden senden",
    "compensationInvoice": "Dies ist eine Schadensersatzrechnung",
    "positions": "Positionen",
    "invoiceTitle": "Rechnungstitel",
    "paymentsMade": "Getätigte Zahlungen",
    "administrativeOffencePositionTitle": "Bearbeitungsgebühr Ordnungswidrigkeit Buchung Aktenzeichen ",
    "saveAndFinalizeInvoice": "Rechnung speichern und abschließen",
    "international": "International"
  },
  "es": {
    "invoiceType": "Tipo de factura",
    "counterpartyType": "Destinatario de la factura (contraparte)",
    "originalInvoice": "Factura original",
    "selectOriginalInvoice": "- Seleccionar factura original -",
    "title": "Título",
    "amount": "Cantidad",
    "unitPrice": "Precio unitario (bruto)",
    "tax": "Impuesto",
    "lanePreset": "Carril contable",
    "counterpartyPreset": "Contraparte",
    "addPosition": "+ Añadir línea",
    "saveInvoice": "Guardar factura",
    "selectPayment": "- Seleccionar pago -",
    "payment": "Pago",
    "repayment": "Reembolso",
    "addPayment": "+ Añadir pago",
    "sendInvoiceOnSave": "Enviar la factura al cliente automáticamente al guardar",
    "compensationInvoice": "Factura de indemnización por daños",
    "positions": "Líneas",
    "invoiceTitle": "Título de la factura",
    "paymentsMade": "Pagos realizados",
    "administrativeOffencePositionTitle": "Gastos de tramitación infracción administrativa expediente reserva n.º ",
    "saveAndFinalizeInvoice": "Guardar y finalizar factura",
    "international": "Internacional"
  }
}
</i18n>
